import { useTheme } from '@emotion/react';
import { ReactComponent as CloseIcon } from 'assets/close_icon.svg';
import { ReactComponent as gpsIcon } from 'assets/gps.svg';
import { FormikProvider, useFormik } from 'formik';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CustomInputField from 'ui-components/formik/CustomInputField';
import CustomAutoComplete from 'ui-components/CustomAutoComplete';
import ModalComponent from 'ui-components/ModalComponent';
import * as Yup from 'yup';
import theme from 'themes/theme';
import CustomLabel from 'ui-components/CustomLabel';
import { useSelector } from 'react-redux';
import {
  FetchAllCities,
  FetchAllCountries,
  FetchAllCountriesWithPhoneCodes,
  FetchAllStates,
} from 'api';
import CustomPhone from 'ui-components/CustomPhone';
import CustomRadio from 'ui-components/CustomRadio';
import { handleFormSubmit } from 'utilities/index';

// ==================== CONSTANTS ====================
const ENTITY_TYPES = {
  INDIVIDUAL: 'INDIVIDUAL',
  BUSINESS: 'BUSINESS',
};

const TAX_TYPES = {
  INTERNATIONAL: 'INTERNATIONAL',
  GST_B2B: 'GST_B2B',
  GST_B2C: 'GST_B2C',
};

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PHONE_REGEX = /^[0-9]{5,15}$/;

// ==================== UTILITY FUNCTIONS ====================
const deriveTaxType = (values) => {
  if (values?.country !== 'India') {
    return TAX_TYPES.INTERNATIONAL;
  }
  if (values?.gstin?.trim()) {
    return TAX_TYPES.GST_B2B;
  }
  return TAX_TYPES.GST_B2C;
};

const getBillingValidationSchema = (entityType, country) => {
  const baseSchema = {
    contact_name: Yup.string().required('Contact name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string()
      .matches(PHONE_REGEX, 'Contact number must be between 5 and 15 digits')
      .required('Contact number is required'),
    entity_type: Yup.string()
      .oneOf(Object.values(ENTITY_TYPES))
      .required('Entity type is required'),
    address_line_1: Yup.string().required('Address is required'),
    address_line_2: Yup.string(),
    zip_code: Yup.string().required('ZIP/Postal code is required'),
    country_id: Yup.string().required('Please select a country'),
    state_id: Yup.string().required('Please select a state'),
    city_id: Yup.string().required('Please select a city'),
  };

  if (country === 'India') {
    if (entityType === ENTITY_TYPES.BUSINESS) {
      baseSchema.gstin = Yup.string().matches(
        GSTIN_REGEX,
        'Invalid GST number format'
      );
      // .required("GST number is required for business entities");
    } else {
      baseSchema.gstin = Yup.string().test(
        'gstin-format',
        'Invalid GST number format',
        (value) => {
          if (!value || value.trim() === '') return true;
          return GSTIN_REGEX.test(value);
        }
      );
    }
  }

  return Yup.object(baseSchema);
};

// ==================== STYLES ====================
const useStyles = makeStyles({
  gradientPaper: {
    // background: `linear-gradient(135deg, ${theme.palette.customColors.b300} 0%, #8B5CF6 100%)`,
    // background: "#fffaf5",
    borderRadius: '24px !important',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    maxWidth: '580px',
    margin: '0 auto',

    backgroundImage:
      'linear-gradient(#fffaf5, #fffaf5), linear-gradient(135deg, #0046ff, #cc5adf)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    border: '2px solid transparent',
  },
  contentSection: {
    backgroundColor: 'white',
    borderRadius: '20px',
    border: '1px solid #EAECF0',
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    color: 'white',
    padding: '6px',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
  },
  primaryButton: {
    background: 'linear-gradient(135deg,#7852f8,#84aef6)',
    // background: `linear-gradient(135deg, ${theme.palette.customColors.b300} 0%, #8B5CF6 100%)`,
    color: 'white',
    borderRadius: '12px',
    padding: '16px 24px',
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    height: '52px',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
    },
  },
  secondaryButton: {
    background: 'rgba(102, 126, 234, 0.08)',
    color: theme?.palette?.customColors?.b300 || '#667eea',
    padding: '14px 24px',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    height: '52px',
    border: 'none',
    '&:hover': {
      background: 'rgba(102, 126, 234, 0.12)',
    },
  },
  sparkleIcon: {
    position: 'absolute',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '1.5rem',
  },
  entityTypeCard: {
    padding: '1rem',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.customColors.b300,
      backgroundColor: 'rgba(102, 126, 234, 0.02)',
    },
    '&.selected': {
      borderColor: theme.palette.customColors.b300,
      backgroundColor: 'rgba(102, 126, 234, 0.05)',
    },
  },
});

// ==================== CUSTOM HOOK: LOCATION STATE ====================
const useLocationState = () => {
  const [locationState, setLocationState] = useState({
    country: null,
    state: null,
    city: null,
    searchCountry: '',
    searchState: '',
    searchCity: '',
  });

  const updateLocation = useCallback((updates) => {
    setLocationState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetStateAndCity = useCallback(() => {
    setLocationState((prev) => ({
      ...prev,
      state: null,
      city: null,
      searchState: '',
      searchCity: '',
    }));
  }, []);

  const resetCity = useCallback(() => {
    setLocationState((prev) => ({
      ...prev,
      city: null,
      searchCity: '',
    }));
  }, []);

  return {
    locationState,
    updateLocation,
    resetStateAndCity,
    resetCity,
  };
};

// ==================== CUSTOM HOOK: LOCATION DATA FETCHING ====================
const useLocationData = (locationState) => {
  const { data: countriesWithPhoneCodes } = FetchAllCountriesWithPhoneCodes({});

  const {
    data: countries,
    isRefetching: isRefetchingCountries,
    isLoading: isLoadingCountries,
    refetch: refetchCountries,
  } = FetchAllCountries({ search: locationState.searchCountry });

  const countryValue = useMemo(() => {
    if (!locationState.country || !countries) return null;
    return countries.find((item) => item?._id === locationState.country?._id)
      ?.value;
  }, [locationState.country, countries]);

  const {
    data: states,
    isRefetching: isRefetchingStates,
    isLoading: isLoadingStates,
    refetch: refetchStates,
  } = FetchAllStates({
    search: locationState.searchState,
    countryID: countryValue,
  });

  const stateValue = useMemo(() => {
    if (!locationState.state || !states) return null;
    return states.find((item) => item?._id === locationState.state?._id)?.value;
  }, [locationState.state, states]);

  const {
    data: cities,
    isRefetching: isRefetchingCities,
    isLoading: isLoadingCities,
    refetch: refetchCities,
  } = FetchAllCities({
    search: locationState.searchCity,
    stateID: stateValue,
    isPopular: 0,
  });

  return {
    countriesWithPhoneCodes,
    countries,
    states,
    cities,
    isLoadingCountries,
    isRefetchingCountries,
    isLoadingStates,
    isRefetchingStates,
    isLoadingCities,
    isRefetchingCities,
    refetchCountries,
    refetchStates,
    refetchCities,
  };
};

// ==================== MAIN COMPONENT ====================
const BillingProfilePopup = ({
  open,
  selectedPlan = null,
  handleClose,
  handleBillingSubmission,
  handleDirectPayment,
  isSubmitting = false,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [showBillingForm, setShowBillingForm] = useState(true);
  const [continueLoading, setContinueLoading] = useState(false);

  const startupDetails = useSelector((state) => state?.startupDetails?.data);
  const userDetails = useSelector((state) => state?.user?.data);

  const { locationState, updateLocation, resetStateAndCity, resetCity } =
    useLocationState();

  const {
    countriesWithPhoneCodes,
    countries,
    states,
    cities,
    isLoadingCountries,
    isRefetchingCountries,
    isLoadingStates,
    isRefetchingStates,
    isLoadingCities,
    isRefetchingCities,
    refetchCountries,
    refetchStates,
    refetchCities,
  } = useLocationData(locationState);

  // ==================== FORMIK INITIALIZATION ====================
  const formik = useFormik({
    initialValues: {
      contact_name:
        startupDetails?.legal_entity_name ||
        startupDetails?.startup_name ||
        userDetails?.name ||
        '',
      email: startupDetails?.email || '',
      phone: startupDetails?.mobile_number || '',
      phone_code: startupDetails?.phone_code || '+91',
      entity_type: ENTITY_TYPES.BUSINESS,
      gstin: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      country_id: '',
      state_id: '',
      city_id: '',
    },
    validationSchema: Yup.lazy((values) =>
      getBillingValidationSchema(values.entity_type, values.country)
    ),
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        tax_type: deriveTaxType(values),
        is_primary: true,
        is_active: true,
        email: values.email,
        contact_name: values.contact_name,
        phone: values.phone,
        phone_code: values.phone_code,
        entity_type: values.entity_type,
        ...(values.gstin && { gstin: values.gstin.toUpperCase() }),
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2 || '',
        city: values.city,
        state: values.state,
        country: values.country,
        city_id: values.city_id,
        state_id: values.state_id,
        country_id: values.country_id,
        zip_code: values.zip_code,
      };
      await handleBillingSubmission(payload);
    },
  });

  // ==================== INITIALIZE LOCATION FROM STARTUP DETAILS ====================
  // useEffect(() => {
  //     if (!startupDetails?.country_id || !countries || countries.length === 0) return;

  //     const foundCountry = countries.find(item => item?._id === startupDetails?.country_id);
  //     if (!foundCountry) return;

  //     updateLocation({ country: foundCountry });
  //     formik.setFieldValue("country", foundCountry.label, false);
  //     formik.setFieldValue("country_id", foundCountry._id, false);

  //     if (startupDetails?.state_id) {
  //         refetchStates().then(stateRes => {
  //             if (!stateRes?.data) return;

  //             const foundState = stateRes.data.find(item => item?._id === startupDetails?.state_id);
  //             if (foundState) {
  //                 updateLocation({ state: foundState });
  //                 formik.setFieldValue("state", foundState.label, false);
  //                 formik.setFieldValue("state_id", foundState._id, false);

  //                 if (startupDetails?.city_id) {
  //                     refetchCities().then(cityRes => {
  //                         if (!cityRes?.data) return;

  //                         const foundCity = cityRes.data.find(item => item?._id === startupDetails?.city_id);
  //                         if (foundCity) {
  //                             updateLocation({ city: foundCity });
  //                             formik.setFieldValue("city", foundCity.label, false);
  //                             formik.setFieldValue("city_id", foundCity._id, false);
  //                         }
  //                     });
  //                 }
  //             }
  //         });
  //     }
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [startupDetails?.country_id, countries]);

  // ==================== EVENT HANDLERS ====================
  const handleCountryChange = useCallback(
    (event, value) => {
      updateLocation({
        country: value,
        searchCountry: value?.label || '',
      });
      resetStateAndCity();

      formik.setFieldValue('country_id', value?._id || '', true);
      formik.setFieldValue('country', value?.label || '', true);
      formik.setFieldValue('state_id', '', true);
      formik.setFieldValue('state', '', true);
      formik.setFieldValue('city_id', '', true);
      formik.setFieldValue('city', '', true);
    },
    [updateLocation, resetStateAndCity, formik]
  );

  const handleStateChange = useCallback(
    (event, value) => {
      updateLocation({
        state: value,
        searchState: value?.label || '',
      });
      resetCity();

      formik.setFieldValue('state_id', value?._id || '', true);
      formik.setFieldValue('state', value?.label || '', true);
      formik.setFieldValue('city_id', '', true);
      formik.setFieldValue('city', '', true);
    },
    [updateLocation, resetCity, formik]
  );

  const handleCityChange = useCallback(
    (event, value) => {
      updateLocation({
        city: value,
        searchCity: value?.label || '',
      });

      formik.setFieldValue('city_id', value?._id || '', true);
      formik.setFieldValue('city', value?.label || '', true);
    },
    [updateLocation, formik]
  );

  const handleCloseModal = useCallback(() => {
    setShowBillingForm(false);
    formik.resetForm();
    handleClose();
  }, [formik, handleClose]);

  const handleDirectPaymentClick = useCallback(async () => {
    setContinueLoading(true);
    try {
      await handleDirectPayment({ ...selectedPlan });
    } finally {
      setContinueLoading(false);
    }
  }, [handleDirectPayment, selectedPlan]);

  const handleContactNameChange = useCallback(
    (e) => {
      const value = e.target.value;
      formik.setFieldValue(
        'contact_name',
        value.charAt(0).toUpperCase() + value.slice(1)
      );
    },
    [formik]
  );

  const handleGSTINChange = useCallback(
    (e) => {
      formik.setFieldValue('gstin', e.target.value.toUpperCase());
    },
    [formik]
  );

  const handleEntityTypeChange = useCallback(
    (type) => {
      formik.setFieldValue('entity_type', type);
    },
    [formik]
  );

  // ==================== COMPUTED VALUES ====================
  const isGSTRequired = false;
  // const isGSTRequired = formik.values.entity_type === ENTITY_TYPES.BUSINESS && formik.values.country === 'India';
  const showGSTField = formik.values.country === 'India';
  const showStateField = Boolean(locationState.country);
  const showCityField = Boolean(locationState.state);

  // ==================== RENDER ====================
  return (
    <ModalComponent
      open={open}
      onClose={handleCloseModal}
      style={{
        maxWidth: '600px',
        width: 'calc(100% - 1rem)',
      }}
    >
      <Paper className={classes.gradientPaper}>
        {/* <Box className={classes.sparkleIcon} sx={{ top: '24px', left: '24px' }}>✨</Box>
                <Box className={classes.sparkleIcon} sx={{ top: '44px', right: '64px' }}>⭐</Box> */}

        <IconButton className={classes.closeButton} onClick={handleCloseModal}>
          <CloseIcon width={20} height={20} />
        </IconButton>

        <Box sx={{ padding: '3rem 1.5rem 1.5rem 1.5rem', textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'black',
              fontSize: '2rem',
              fontWeight: 700,
              marginBottom: '1.5rem',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
              letterSpacing: '-0.02em',
            }}
          >
            Billing Details
          </Typography>
        </Box>

        <Box
          className={classes.contentSection}
          sx={{
            padding: { xs: '1rem 1rem', md: '2rem 1.75rem' },
            margin: {
              xs: '0 0.75rem 0.75rem 0.75rem',
              md: '0 1.25rem 1.25rem 1.25rem',
            },
          }}
        >
          {/* {!showBillingForm ? (
                        <SelectionScreen 
                            classes={classes}
                            isSubmitting={isSubmitting}
                            continueLoading={continueLoading}
                            onShowBillingForm={() => setShowBillingForm(true)}
                            onDirectPayment={handleDirectPaymentClick}
                        />
                    ) : ( */}
          <BillingForm
            classes={classes}
            formik={formik}
            isSubmitting={isSubmitting}
            countriesWithPhoneCodes={countriesWithPhoneCodes}
            countries={countries}
            states={states}
            cities={cities}
            locationState={locationState}
            updateLocation={updateLocation}
            isLoadingCountries={isLoadingCountries}
            isRefetchingCountries={isRefetchingCountries}
            isLoadingStates={isLoadingStates}
            isRefetchingStates={isRefetchingStates}
            isLoadingCities={isLoadingCities}
            isRefetchingCities={isRefetchingCities}
            refetchCountries={refetchCountries}
            refetchStates={refetchStates}
            refetchCities={refetchCities}
            handleCountryChange={handleCountryChange}
            handleStateChange={handleStateChange}
            handleCityChange={handleCityChange}
            handleContactNameChange={handleContactNameChange}
            handleGSTINChange={handleGSTINChange}
            handleEntityTypeChange={handleEntityTypeChange}
            isGSTRequired={isGSTRequired}
            showGSTField={showGSTField}
            showStateField={showStateField}
            showCityField={showCityField}
          />
          {/* )} */}
        </Box>
      </Paper>
    </ModalComponent>
  );
};

// ==================== SUB-COMPONENTS ====================
const SelectionScreen = React.memo(
  ({
    classes,
    isSubmitting,
    continueLoading,
    onShowBillingForm,
    onDirectPayment,
  }) => (
    <Box>
      <Typography
        sx={{
          fontSize: '0.95rem',
          color: '#6B7280',
          textAlign: 'center',
          lineHeight: 1.6,
          marginBlock: '1rem 2rem',
        }}
      >
        Add your business details to receive a tax-compliant invoice for your
        payment.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            className={classes.primaryButton}
            component="button"
            type="button"
            disabled={isSubmitting || continueLoading}
            sx={{
              width: '100%',
              border: 'none',
              cursor: isSubmitting || continueLoading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: isSubmitting || continueLoading ? 'none' : 'auto',
            }}
            onClick={onShowBillingForm}
          >
            Continue With Billing Details
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box
            className={classes.secondaryButton}
            component="button"
            type="button"
            disabled={isSubmitting || continueLoading}
            sx={{
              width: '100%',
              cursor: isSubmitting || continueLoading ? 'default' : 'pointer',
              pointerEvents: isSubmitting || continueLoading ? 'none' : 'auto',
            }}
            onClick={onDirectPayment}
          >
            {continueLoading ? (
              <CircularProgress
                sx={{ height: '24px !important', width: '24px !important' }}
              />
            ) : (
              'Continue without Billing Details'
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
);

SelectionScreen.displayName = 'SelectionScreen';

const BillingForm = React.memo(
  ({
    classes,
    formik,
    isSubmitting,
    countriesWithPhoneCodes,
    countries,
    states,
    cities,
    locationState,
    updateLocation,
    isLoadingCountries,
    isRefetchingCountries,
    isLoadingStates,
    isRefetchingStates,
    isLoadingCities,
    isRefetchingCities,
    refetchCountries,
    refetchStates,
    refetchCities,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
    handleContactNameChange,
    handleGSTINChange,
    handleEntityTypeChange,
    isGSTRequired,
    showGSTField,
    showStateField,
    showCityField,
  }) => {
    return (
      <FormikProvider value={formik}>
        <form
          onSubmit={(e) => handleFormSubmit(e, formik)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        >
          <Grid
            container
            columnSpacing={1}
            rowSpacing={0}
            sx={{
              maxHeight: {
                xs: 'calc(100dvh - 12rem)',
                sm: 'calc(100dvh - 15rem)',
                md: 'calc(100dvh - 20rem)',
                lg: 'calc(100dvh - 30rem)',
              },
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#D1D5DB',
                borderRadius: '3px',
              },
            }}
          >
            {/* 1. Entity Type Selection */}
            <Grid item xs={12}>
              <EntityTypeSelector
                classes={classes}
                selectedType={formik.values.entity_type}
                onSelect={handleEntityTypeChange}
              />
            </Grid>

            {/* 2. Contact Name */}
            <Grid item xs={12}>
              <CustomLabel>Billing Name</CustomLabel>
              <CustomInputField
                name="contact_name"
                placeholder="Enter billing name"
                value={formik.values.contact_name}
                onChange={handleContactNameChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </Grid>

            {/* 3. Email */}
            <Grid item xs={12}>
              <CustomLabel>Email Address</CustomLabel>
              <CustomInputField
                name="email"
                type="email"
                placeholder="Enter email address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </Grid>

            {/* 4. Contact Number */}
            {formik.values.phone_code && (
              <Grid item xs={12}>
                <CustomLabel>Contact Number</CustomLabel>
                <CustomPhone
                  selectedCode={formik.values.phone_code}
                  handleChange={(event) =>
                    formik.setFieldValue('phone_code', event.target.value)
                  }
                  countryCodes={countriesWithPhoneCodes || []}
                  name="phone"
                  placeholder="Enter mobile number"
                  value={formik.values.phone}
                  onChange={(e) =>
                    formik.setFieldValue('phone', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </Grid>
            )}

            {/* 5. Country */}
            <Grid item xs={12}>
              <CustomAutoComplete
                options={
                  !isLoadingCountries && !isRefetchingCountries ? countries : []
                }
                label="SELECT COUNTRY"
                name="country_id"
                noOptionsText={
                  !isLoadingCountries && !isRefetchingCountries
                    ? 'No Options'
                    : 'Searching...'
                }
                placeholder="Type a country name"
                icon={gpsIcon}
                handleSearchChange={(event, value) =>
                  updateLocation({ searchCountry: value })
                }
                handleSelectionChange={handleCountryChange}
                search={locationState.searchCountry}
                value={locationState.country?.label || ''}
                defaultValue={locationState.country}
                loading={isRefetchingCountries}
                // handleSearchUpdate={refetchCountries}
                autoComplete="off"
              />
            </Grid>

            {/* 6. State */}
            {showStateField && (
              <Grid item xs={12}>
                <CustomAutoComplete
                  options={
                    !isLoadingStates &&
                    !isRefetchingStates &&
                    states?.length > 0
                      ? states
                      : []
                  }
                  label="SELECT STATE"
                  placeholder="Type a state name"
                  name="state_id"
                  noOptionsText={
                    !isLoadingStates && !isRefetchingStates
                      ? 'No Options'
                      : 'Searching...'
                  }
                  icon={gpsIcon}
                  handleSearchChange={(event, value) =>
                    updateLocation({ searchState: value })
                  }
                  handleSelectionChange={handleStateChange}
                  search={locationState.searchState}
                  value={locationState.state?.label || ''}
                  defaultValue={locationState.state}
                  loading={isRefetchingStates}
                  // handleSearchUpdate={refetchStates}
                  autoComplete="off"
                />
              </Grid>
            )}

            {/* 7. City */}
            {showCityField && (
              <Grid item xs={12}>
                <CustomAutoComplete
                  options={
                    !isLoadingCities &&
                    !isRefetchingCities &&
                    cities?.length > 0
                      ? cities
                      : []
                  }
                  noOptionsText={
                    !isLoadingCities && !isRefetchingCities
                      ? 'No Options'
                      : 'Searching...'
                  }
                  label="SELECT CITY"
                  placeholder="Type a city name"
                  name="city_id"
                  icon={gpsIcon}
                  handleSearchChange={(event, value) =>
                    updateLocation({ searchCity: value })
                  }
                  handleSelectionChange={handleCityChange}
                  search={locationState.searchCity}
                  value={locationState.city?.label || ''}
                  defaultValue={locationState.city}
                  loading={isRefetchingCities}
                  // handleSearchUpdate={refetchCities}
                  autoComplete="off"
                />
              </Grid>
            )}

            {/* 8. Address Line 1 */}
            <Grid item xs={12}>
              <CustomLabel>Address Line 1</CustomLabel>
              <CustomInputField
                name="address_line_1"
                placeholder="Street, building, etc."
                value={formik.values.address_line_1}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </Grid>

            {/* 9. Address Line 2 */}
            <Grid item xs={12}>
              <CustomLabel>Address Line 2 (Optional)</CustomLabel>
              <CustomInputField
                name="address_line_2"
                placeholder="Locality, landmark"
                value={formik.values.address_line_2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </Grid>

            {/* 10. ZIP Code */}
            <Grid item xs={12}>
              <CustomLabel>ZIP / Postal Code</CustomLabel>
              <CustomInputField
                name="zip_code"
                placeholder="Enter zip code"
                value={formik.values.zip_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </Grid>

            {/* 11. GST Number */}
            {showGSTField && (
              <Grid item xs={12}>
                <CustomLabel>
                  GST Number {isGSTRequired ? '' : '(Optional)'}
                </CustomLabel>
                <CustomInputField
                  name="gstin"
                  placeholder="Enter GST number"
                  value={formik.values.gstin}
                  onChange={handleGSTINChange}
                  onBlur={formik.handleBlur}
                  required={isGSTRequired}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                {!isGSTRequired && !formik.errors?.gstin && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      marginBottom: '14.5px',
                      marginTop: '-1rem',
                      marginLeft: '14px',
                    }}
                  >
                    Provide GST number if you want GST invoice
                  </Typography>
                )}
              </Grid>
            )}

            {/* 12. Submit Button */}
            <Grid item xs={12} sx={{ marginTop: '0.5rem' }}>
              <Box
                className={classes.primaryButton}
                component="button"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  width: '100%',
                  border: 'none',
                  cursor: isSubmitting ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: isSubmitting ? 'none' : 'auto',
                }}
              >
                {isSubmitting ? (
                  <CircularProgress
                    sx={{
                      height: '24px !important',
                      width: '24px !important',
                      color: 'white',
                    }}
                  />
                ) : (
                  'Continue'
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormikProvider>
    );
  }
);

BillingForm.displayName = 'BillingForm';

const EntityTypeSelector = React.memo(({ classes, selectedType, onSelect }) => (
  <>
    <CustomLabel>I am purchasing as</CustomLabel>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Box
          className={`${classes.entityTypeCard} ${selectedType === ENTITY_TYPES.BUSINESS ? 'selected' : ''} mb-3`}
          onClick={() => onSelect(ENTITY_TYPES.BUSINESS)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomRadio
              checked={selectedType === ENTITY_TYPES.BUSINESS}
              value={ENTITY_TYPES.BUSINESS}
              size="small"
            />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Business
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                Company or registered entity
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        sx={{
          paddingBottom: { xs: '1rem !important', sm: '0rem !important' },
          paddingTop: { xs: '0rem !important', sm: '1rem !important' },
        }}
      >
        <Box
          className={`${classes.entityTypeCard} ${selectedType === ENTITY_TYPES.INDIVIDUAL ? 'selected' : ''}`}
          onClick={() => onSelect(ENTITY_TYPES.INDIVIDUAL)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomRadio
              checked={selectedType === ENTITY_TYPES.INDIVIDUAL}
              value={ENTITY_TYPES.INDIVIDUAL}
              size="small"
            />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Individual
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                Personal use
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  </>
));

EntityTypeSelector.displayName = 'EntityTypeSelector';

export default BillingProfilePopup;
